import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { tap } from 'rxjs/operators';
import { HierarchyService } from 'src/app/core/hierarchy.service';
import { PosizioniService } from 'src/app/core/posizioni.service';
import { Posizione } from 'src/app/shared/models/posizione.model';

@Component({
  selector: 'app-muovi-posizione-dialog',
  template: `
  <p-dialog [(visible)]="isModalActive" (onHide)="closeDialog.emit()" [modal]="true" [closable]="true" [closeOnEscape]="false" [style]="{'width.px': '500'}">
    <p-header>
      <span style="font-weight:normal">Sposta in nuova posizione</span>
    </p-header>
    <div class="grid">
      <form [formGroup]="form">
        <ng-container *ngFor="let level of hierarchy; let i = index">
          <div class="col-12" [class.error]="isInErrorStateField(i)">
            <label class="small-label">{{level}}</label>
            <p-autoComplete
                [suggestions]="autocompleteValues[i]"
                [formControlName]="i"
                delay="20"
                size="50"
                appendTo="body"
                [dropdown]="true"
                [forceSelection]="true"
                field="title"
                (completeMethod)="autocomplete($event, i)"
                (onSelect)="selectItem($event.value, i)"
                [autoHighlight]="true"
                [inputStyle]="{'width.px': '420'}">
            </p-autoComplete>
          </div>
        </ng-container>
      </form>
    </div>
    <p-footer>
      <button pButton type="button" label="Annulla" class="p-button-secondary" (click)="closeMoveNodeModal()"></button>
      <button pButton type="button" label="Sposta" [disabled]="!isSaveActive" (click)="saveMoveNode()"></button>
    </p-footer>
  </p-dialog>
  `,
  styles: [`
  `]
})
export class MuoviPosizioneDialogComponent implements OnInit {
  @Input() disableFromLevel = -1;
  @Input() stopBelowLevels = -1;

  @Input() set isMoveDialogActive(isActive: boolean) {
    this.form.reset();
    this.isModalActive = isActive;
    if (this.isModalActive) {
      this.initDropdownValues();
    }
  }

  @Output() save = new EventEmitter<string>();
  @Output() closeDialog = new EventEmitter<void>();

  public isSaveActive = false;
  public nodeTo = null;

  public hierarchy: string[];
  public autocompleteValues: Posizione[][];
  public filteredValues: Posizione[][];
  public posizione: Posizione[];
  public form = new FormGroup({});
  public isModalActive = false;

  constructor(
    private hierarchyService: HierarchyService,
    private posizioniService: PosizioniService) { }

  ngOnInit(): void {
    this.form.valueChanges.pipe(
      tap(() => {
        this.isSaveActive = this.form.valid && this.isFormValid();
      })
    ).subscribe();
  }

  closeMoveNodeModal(): void {
    this.closeDialog.emit();
    this.disableFromLevel = -1;
  }

  saveMoveNode(): void {
    const frm = this.form.get((this.hierarchy.length - 1).toString());
    if (frm === null) { return; }
    this.save.emit((frm.value as Posizione).posizioneId);
  }

  initDropdownValues(): void {
    this.hierarchy = [...this.hierarchyService.getHierarchyLabels()];

    if (this.stopBelowLevels > 0) {
      const removeNPositions = this.hierarchy.length - this.stopBelowLevels;
      for (let i = 0; i < removeNPositions; i++) { this.hierarchy.pop(); }
    }

    this.autocompleteValues = new Array<Posizione[]>(this.hierarchy.length);
    this.filteredValues = new Array<Posizione[]>(this.hierarchy.length);

    this.hierarchy.forEach((_, index) => {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.form.addControl(index.toString(), new FormControl({ value: null, disabled: index > 0 }, Validators.required));
    });
  }

  autocomplete(event: { query: string }, level: number): void {
    const value = event.query.toLowerCase();
    const parent = this.getParent(level);
    let httpApi = this.posizioniService.getAutocompleteForLevel(level, value, 20);

    if (parent) {
      httpApi = this.posizioniService.getAutocompleteForPath(parent, value, 20);
    }

    httpApi.pipe(
      tap((f) => this.processAutocompleteResult(f, level, value))
    ).subscribe();

  }

  selectItem(value: Posizione, level: number): void {
    this.resetChildren(level);
    const childLevel = level + 1;
    if (childLevel < this.hierarchy.length) {
      this.posizioniService.getAutocompleteForPath(value.posizioneId, '', 0).subscribe(f => {
        this.processAutocompleteResult(f, childLevel, '');
      });
    }
  }

  isInErrorStateField(controlName: number): boolean {
    const field = this.form.get(controlName.toString());
    return field === null ? false : field.invalid && (field.dirty || field.touched);
  }

  private isFormValid() {
    let result = this.form.valid;
    if (!result) { return result; }
    // Check if any elements is disabled
    this.hierarchy?.forEach((_, index) => {
      result &&= !!this.form.get(index.toString())?.enabled;
    });

    return result;
  }

  private resetChildren(parentLevel: number) {
    for (let level = parentLevel + 1; level < this.hierarchy.length; level++) {
      this.filteredValues[level] = [];
      const currentForm = this.form.get(level.toString());
      if (currentForm === null) { return; }
      currentForm.reset()
      currentForm.disable();
    }
  }

  private getParent(level: number): string | null {
    level--;
    const frm = this.form.get(level.toString());
    if (!frm?.value) { return null; }
    return (frm.value as Posizione).posizioneId;
  }

  private mapPosizioni(posizioni: Posizione[]) {
    return posizioni.map(p => {
      p.title = p.descrizione ? `${p.sigla} - ${p.descrizione.slice(0, 20)}` : `${p.sigla}`;
      return p;
    });
  }

  private escludiChiusi(posizioni: Posizione[]) {

    if (window.location.pathname === '/nuovo') {
      return posizioni.filter(node => node.chiuso === false);
    } else {
      return posizioni;
    }
  }

  private processAutocompleteResult(f: Posizione[], level: number, value: string) {
    let posizioni = this.mapPosizioni(f);
    posizioni = this.escludiChiusi(posizioni);
    this.autocompleteValues[level] = posizioni;
    if (value === '') {
      this.filteredValues[level] = posizioni;
    }

    const currentForm = this.form.get(level.toString());
    if (currentForm === null) { return; }

    if (posizioni.length === 0) {
      currentForm.reset();
      currentForm.disable();
    } else {
      currentForm.enable();
    }
  }
}
